import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import KoiiHelp from './commands/KoiiHelp';
import KoiiEpoch from './commands/KoiiEpoch';
import KoiiBlockHeight from './commands/KoiiBlockHeight';
import KoiiValidators from './commands/KoiiValidators';
import KoiiAirdrop from './commands/KoiiAirdrop';
import KoiiBalance from './commands/KoiiBalance';
import KoiiAccount from './commands/KoiiAccount';
import KoiiAddress from './commands/KoiiAddress';

// Styles for the terminal container and its components
const TerminalContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100vh;
  min-height: 100vh;
  min-width: 100vw; 
  padding: 10px;
  box-sizing: border-box;
  background-color: #2c001e;
  overflow: hidden;
`;

const OutputContainer = styled.div`
  flex-grow: 1;
  white-space: pre-wrap;
  color: #fff;
  font-family: 'Courier New', Courier, monospace;
  overflow-y: auto;   /* Add scroll if content overflows */
  background-color: #2c001e; 
  height: 100%;  /* Make sure the height is 100% of the available space */
  padding-bottom: 10px;  /* Optional: Adjust if you need padding */
`;

const PromptContainer = styled.div`
  display: flex;
  align-items: center;
  font-family: 'Courier New', Courier, monospace;
  font-size: 1.2rem;
`;

const PromptTextBold = styled.span`
  font-weight: bold;
`;

const GreenText = styled(PromptTextBold)`
  color: #4ee44e;
`;

const WhiteText = styled(PromptTextBold)`
  color: #ffffff;
`;

const BlueText = styled(PromptTextBold)`
  color: #1e90ff;
`;

const InputField = styled.input`
  background-color: transparent;
  color: #fff;
  border: none;
  outline: none;
  font-size: 1.2rem;
  margin-left: 5px;
  font-family: 'Courier New', Courier, monospace;

  ::placeholder {
    color: rgba(128, 128, 128, 0.5);  // Semi-transparent gray
    font-style: italic !important;     // Force italic with !important
    font-weight: 600 !important;       // Force semi-bold with !important
  }
`;

// Output not yet ready
const RedText = styled(PromptTextBold)`
  color: rgba(255, 0, 0, 0.666);
  font-style: italic;
`;

function App() {
  const [input, setInput] = useState('');
  const [outputHistory, setOutputHistory] = useState([]);  // Track history of the last two inputs and outputs
  const inputRef = useRef(null);

  // UseEffect hook to focus input when clicking anywhere on the document
  useEffect(() => {
    const handleDocumentClick = () => {
      if (inputRef.current) {
        inputRef.current.focus();  // Focus the input field
      }
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);  // Cleanup listener
    };
  }, []);

  // Function to handle input change
  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  // Function to handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Create the new prompt and input display
    const newPrompt = (
      <div key={Date.now()}>
        <PromptContainer>
          <GreenText>node@koii</GreenText>
          <WhiteText>:</WhiteText>
          <BlueText>{'/home'}</BlueText>
          <WhiteText>$</WhiteText>
          <span>{input}</span>
        </PromptContainer>
      </div>
    );

    // Handling various commands
    let commandOutput;
    switch(input.trim()) { // Trim input to remove unnecessary spaces
      case "koii help":
        commandOutput = <KoiiHelp key={Date.now() + 1} />;
        break;
      case "koii epoch":
        commandOutput = <KoiiEpoch key={Date.now() + 1} />;
        break;
      case "koii block height":
        commandOutput = <KoiiBlockHeight key={Date.now() + 1} />;
        break;
      case "koii validators":
        commandOutput = <KoiiValidators key={Date.now() + 1} />;
        break;
      case "koii airdrop":
        commandOutput = <KoiiAirdrop key={Date.now() + 1} />;
        break;
      case "koii balance":
        commandOutput = <KoiiBalance key={Date.now() + 1} />;
        break;
      case "koii account":
        commandOutput = <KoiiAccount key={Date.now() + 1} />;
        break;
      case "koii address":
        commandOutput = <KoiiAddress key={Date.now() + 1} />;
        break;
      case "koii commands":
        commandOutput = (
          <div key={Date.now() + 1}>
            Available commands:
            <br />- koii help
            <br />- koii epoch
            <br />- koii validators
            <RedText><br />- koii block height</RedText>
            <RedText><br />- koii airdrop</RedText>
            <RedText><br />- koii balance</RedText>
            <RedText><br />- koii account</RedText>
            <RedText><br />- koii address</RedText>
          </div>
        );
        break;
      case "clear":
        setOutputHistory([]); // Clear the output history
        setInput(''); // Clear the input field
        return; // Exit early to avoid adding the "clear" command to output
      default:
        commandOutput = <div key={Date.now() + 1}>Invalid command</div>;
        break;
    }

    // Update the output history by appending the new prompt and output at the end, keeping only the last two entries
    setOutputHistory((prevHistory) => {
      const newHistory = [...prevHistory, newPrompt, commandOutput];
      return newHistory.slice(-4); // Keep only the last two command-output pairs (4 items in total)
    });

    setInput('');  // Clear the input after submission
  };

  return (
    <TerminalContainer>
      <OutputContainer>
        {outputHistory.map((output, index) => (
          <div key={index}>{output}</div> // Display each output in the history
        ))}
      </OutputContainer>
      <form onSubmit={handleSubmit}>
        <PromptContainer>
          <GreenText>node@koii</GreenText>
          <WhiteText>:</WhiteText>
          <BlueText>{'/home'}</BlueText>
          <WhiteText>$</WhiteText>
          <InputField
            type="text"
            value={input}
            onChange={handleInputChange}
            placeholder="koii commands"
            autoFocus
            ref={inputRef}  // Set the reference to the input field
          />
        </PromptContainer>
      </form>
    </TerminalContainer>
  );
}

export default App;
